import posthog from "posthog-js";
import { z } from "zod";
import { captureCaughtError } from "../sentry";
import {
  ACTIVATE_PATH,
  ENTER_PATH,
  LOGIN_PATH,
  LOGOUT_PATH,
  ONBOARDING_PATH,
  RESET_PASSWORD_PATH,
  SUPPLIER_MATCH_CONFIRM_PATH
} from "../navigation/routes";
import type { ActorType } from "./actor";
import { clearActorType, getActorType, setActorType } from "./actor";
import { SENSITIVE_QUERY_PARAMS, sanitiseEvent, sanitiseUrl } from "./sanitise";

export type { ActorType } from "./actor";

// `./actor` is an INTERNAL seam. Its accessors are deliberately not re-exported:
// storing the actor is a step in a session transition, never a thing a caller
// does on its own, and the transitions below are the interface. See
// `beginConciergeSession` for what that buys.
//
// `readActorType` below is the one deliberate exception: a narrow, explicitly
// read-only widening of that surface for UI gating (KAN-193), not a bypass of
// it. See its own doc comment for why this is a conscious choice rather than
// a caller reaching into `./actor` directly.

/**
 * Single boundary for product analytics (PostHog).
 *
 * Components never import `posthog` directly - they call the typed `analytics`
 * methods exported below. Each method is the only place that knows a PostHog
 * event name and payload shape, so the event catalog is self-documenting and
 * swapping providers later touches this module alone.
 *
 * Payloads are validated against Zod schemas in development only; in production
 * validation is skipped for zero runtime overhead.
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";

let initialized = false;

/** posthog's persisted property recording whether `identify` has run. */
const USER_STATE_PROPERTY = "$user_state";

/**
 * Set once an operator session is identified, and never unset for the life of
 * the page. Separate from `initialized` so a late suppression (the actor is
 * only known after redemption, by which point the app shell has already
 * initialised) cannot be undone by a subsequent `initAnalytics` call.
 */
let suppressed = false;

/**
 * Class marking an element whose **rendered text** must not reach a session
 * replay. Import this rather than hand-writing the string, so every masked
 * surface is greppable from one place (ADR-0006).
 *
 * Masks the element and its descendants, so it belongs on the container - the
 * address block, not each line of it.
 */
export const PII_MASK_CLASS = "ph-mask-pii";

const PII_MASK_SELECTOR = `.${PII_MASK_CLASS}`;

/**
 * Class marking an element that must not reach a session replay **at all** -
 * it is recorded as an empty placeholder of the same size, not as masked text.
 *
 * Stronger than {@link PII_MASK_CLASS} and used only where the content is
 * sensitive rather than merely identifying: today, the card-entry surface
 * (KAN-44 §9). Prefer the mask elsewhere - blocking leaves a hole in the replay,
 * and a replay full of holes answers none of the questions recording is for.
 *
 * This is posthog's own built-in `blockClass` default, so unlike the mask
 * selector it needs no `session_recording` config to take effect.
 */
export const PII_BLOCK_CLASS = "ph-no-capture";

/**
 * Class keeping clicks inside an element out of `$dead_click` events.
 *
 * A dead click is captured with the clicked element's text (`$el_text`) and its
 * ancestors' element chain. Autocapture being off does not stop that - dead
 * click capture is its own module (`capture_dead_clicks`) - so a surface whose
 * text must never leave the browser outside the replay mask needs this too.
 * posthog honours it on the clicked element or any ancestor, and it is the
 * dead-click module's own default ignore class, so it needs no config. Today:
 * the order assistant drawer and its supplier detail view, whose text is a
 * model's answer.
 *
 * It does not touch session replay (use {@link PII_MASK_CLASS}) or heatmaps.
 */
export const NO_DEAD_CLICK_CLASS = "ph-no-deadclick";

/**
 * Whether analytics is active. False when no key is configured (local/dev/
 * staging), which makes every analytics call a complete no-op - no network
 * requests and no cookies.
 */
export function isAnalyticsEnabled(): boolean {
  return initialized;
}

/**
 * Initialise PostHog once. No-op when VITE_POSTHOG_KEY is unset, so nothing is
 * loaded or captured in unconfigured environments. Called by AnalyticsProvider.
 */
export function initAnalytics(): void {
  if (initialized || suppressed || !POSTHOG_KEY) return;

  // The operator blackout (KAN-16 decision 8, as revised). An operator shops
  // **as the salon's own customer id**, so `identifyUser(customerId)` would
  // bind Caroline's cart-building to the salon's person - her searches and
  // product views would land in the salon's history as if the salon had made
  // them, poisoning the funnel the pilot exists to measure. Labelling the
  // events cannot fix that; only not recording them can.
  if (getActorType() === "concierge_operator") {
    suppressed = true;
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Curated named events only - we never rely on DOM-based autocapture.
    //
    // This costs us `$rageclick` as an *event*: its detector lives inside the
    // autocapture module and is gated on it. The heatmaps module runs a second,
    // independent rage-click detector, so rage clicks still surface on the
    // heatmap - they just cannot be a funnel step, a filter or a replay marker.
    // Accepted deliberately rather than turning autocapture on to win back one
    // event (KAN-44 R4, as amended).
    autocapture: false,
    // Page views are emitted manually from the TanStack Router (see
    // AnalyticsProvider); posthog's history-based capture is unreliable with
    // client-side routers.
    capture_pageview: false,
    // Fires once per *page session*, at unload - never on SPA route changes,
    // whose detection is gated on `capture_pageview: "history_change"`. Kept on
    // because it is the only thing that times the **last** route of a session:
    // route durations ride on the *next* `$pageview`, and the final route has
    // no next pageview. For a drop-off funnel that is the route that matters
    // most - the screen they quit from. `_shouldCapturePageleave` honours an
    // explicit `true` regardless of `capture_pageview`.
    capture_pageleave: true,
    // Frustration signals (KAN-44 R4/R5). All three fall back to a PostHog
    // remote setting when left undefined; set explicitly so the behaviour lives
    // in the source rather than in a dashboard nobody thinks to check.
    capture_exceptions: true,
    capture_heatmaps: true,
    capture_dead_clicks: true,
    // No URL leaves the browser with its query string (KAN-253): a concierge
    // link carries a single-use credential in `/enter?token=...` and posthog
    // stamps the full URL on nearly everything it sends. `before_send` is the
    // one hook that sees the finished payload - event properties, `$set`,
    // `$set_once` and the heatmap data - for every event posthog emits itself.
    //
    // It is also where the operator blackout is enforced for events posthog
    // raises on its own (pageleave, exceptions, heatmaps, flag calls, replay),
    // which never pass through `captureEvent`'s gate: once `suppressed` is
    // latched every event is dropped here, whatever posthog's own consent state
    // says. See `suppressAnalytics`.
    before_send: (event) => (suppressed ? null : sanitiseEvent(event)),
    // Second layer, at the source. posthog posts its stored initial person
    // properties to `/flags` with no hook in between, so the credential is also
    // masked in the stored copy. Needs `mask_personal_data_properties` on.
    mask_personal_data_properties: true,
    custom_personal_data_properties: SENSITIVE_QUERY_PARAMS,
    // Recording never starts at init - see `startSessionReplay`. The session's
    // actor is only known after a concierge link is redeemed, and an operator
    // session must never be recorded.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      // Input masking does NOT cover rendered text, and these blocks render the
      // salon's own business details (ADR-0006). Nested under `session_recording`
      // deliberately: at the top level of `init` these options are accepted and
      // then silently ignored.
      maskTextSelector: PII_MASK_SELECTOR,
      // Replay snapshots are compressed, so `before_send` cannot reach the page
      // URL rrweb records. posthog routes it through this hook instead (the
      // field is typed for network requests; `name` is the URL either way).
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: sanitiseUrl(request.name)
      })
    }
  });

  // There is no consent UI, so a persisted opt-out in this browser can only be
  // one we wrote ourselves, and it must not outlive the operator session that
  // wrote it: posthog stores it (`__ph_opt_in_out_<token>`) and would keep the
  // browser dark for self-serve logins and salon concierge sessions after it.
  // `suppressAnalytics` no longer writes one (see there), so this only cleans
  // up a browser that carries one from earlier. `captureEventName: false`
  // skips the `$opt_in` event opting back in would otherwise emit.
  if (posthog.get_explicit_consent_status() === "denied") {
    posthog.opt_in_capturing({ captureEventName: false });
  }

  initialized = true;
}

/**
 * PostHog feature flag gating session replay.
 *
 * This flag **is** the per-salon opt-out (KAN-44 R8): switching it off for one
 * person stops recording them without a deploy. PostHog's own project-level
 * replay toggle remains the global kill switch.
 *
 * Fails closed. `isFeatureEnabled` returns `undefined` while flags are still
 * loading and for a flag that does not exist, both of which read as "do not
 * record" - so an environment where the flag has never been created records
 * nobody. That is the intended default for a privacy-sensitive capability, and
 * it does mean replay stays dark until the flag is created in PostHog.
 */
export const SESSION_REPLAY_FLAG = "session-replay";

/**
 * Local escape hatch: set `VITE_POSTHOG_SESSION_REPLAY=false` to keep replay off
 * while leaving events on.
 *
 * For working against a real PostHog project locally. Unsetting
 * `VITE_POSTHOG_KEY` already disables everything, but that also silences the
 * events you are usually there to test - and since local shares the dev
 * project, the `session-replay` flag would otherwise record your own screen
 * alongside the salons'.
 *
 * **Not a privacy control.** It is a build-time Vite value, so changing it in a
 * deployed environment means a rebuild and redeploy - a release, not a switch,
 * which is exactly what KAN-44 R8 rejected. The production controls remain the
 * feature flag (per person) and PostHog's project-level replay toggle (global),
 * both of which take effect server-side and immediately.
 */
const SESSION_REPLAY_DISABLED_LOCALLY =
  import.meta.env.VITE_POSTHOG_SESSION_REPLAY === "false";

let replayStarted = false;

/**
 * Start session replay for a **salon** session, once the flag allows it.
 *
 * Only call this when the session's actor is settled. Operator sessions are
 * already excluded twice over - `initAnalytics` refuses to start for a stored
 * operator actor, and this returns early when suppressed - but a session whose
 * actor is not yet *known* (a concierge link mid-redemption) would default to
 * the salon and start recording an operator for the second or two before
 * redemption resolves. Callers own that timing; see AnalyticsProvider.
 */
export function startSessionReplay(): void {
  if (!initialized || suppressed || replayStarted) return;
  if (SESSION_REPLAY_DISABLED_LOCALLY) return;

  // Flags may not have loaded yet, so this cannot be a plain `if`. The callback
  // also re-fires whenever flags reload, which is what lets an opt-out applied
  // mid-session take effect on the next load rather than never.
  // Held on an object rather than in a `const` the callback closes over:
  // `onFeatureFlags` runs its callback *synchronously* when flags are already
  // cached, which is before the returned handle exists. A direct self-reference
  // would throw on exactly those sessions.
  const listener: { unsubscribe?: () => void; started: boolean } = {
    started: false
  };

  listener.unsubscribe = posthog.onFeatureFlags(() => {
    if (replayStarted || suppressed) return;
    if (!posthog.isFeatureEnabled(SESSION_REPLAY_FLAG)) return;

    replayStarted = true;
    listener.started = true;
    posthog.startSessionRecording();
    listener.unsubscribe?.();
  });

  // The synchronous case again: the callback above could not unsubscribe
  // itself, because the handle did not exist yet. It does now.
  if (listener.started) listener.unsubscribe();
}

/**
 * Stop recording for this page, for an operator session discovered late.
 *
 * The actor is only known once a concierge link is redeemed. The app shell
 * leaves `/enter` uninitialised so normally there is nothing to stop, but this
 * also covers any path that initialised before the actor was known. It latches
 * `suppressed`, which both gates `initAnalytics` for the rest of the page and
 * makes `before_send` drop every event posthog still tries to send.
 *
 * Deliberately does **not** call `posthog.opt_out_capturing()`. That persists in
 * the browser (`__ph_opt_in_out_<token>`) and nothing would ever opt back in,
 * and it is not what holds the line anyway: `posthog.reset()` below clears the
 * stored consent again, so the opt-out never survived the call that followed it.
 * The page-lifetime latch plus the stored actor (which keeps later page loads
 * dark) is the whole blackout, and neither depends on posthog's consent state.
 *
 * Safe to call when analytics was never enabled - it just latches the flag.
 */
export function suppressAnalytics(): void {
  suppressed = true;
  if (!initialized) return;
  // Defensive: callers are expected to keep replay from starting before the
  // actor is known, so there should be nothing to stop here. If that ever slips,
  // silently recording an operator is the worst failure this module has.
  posthog.stopSessionRecording();
  posthog.reset();
  initialized = false;
}

/**
 * Read-only accessor for the current session's actor type, for UI gating.
 *
 * Unlike `getActorType`/`setActorType`/`clearActorType` in `./actor` - which
 * remain a deliberately un-exported internal seam, because storing the actor
 * is a step in a session transition, never a thing a caller does on its own -
 * this is a conscious, narrow widening of the public surface: UI that needs
 * to branch on "is this session an operator's" (KAN-193's ad hoc paste-in
 * entry point is the first such surface, see `useIsConciergeOperator`) may
 * read the actor type this way, but must never call the transition functions
 * directly.
 *
 * Still just a display/analytics dimension, same caveat as everywhere else in
 * this module: nothing about it is trusted for authorization, and the server
 * never reads it back. Gating a genuinely sensitive action (like the ad hoc
 * add call) must additionally check for a real authorization artifact - see
 * `lib/concierge/capability-token.ts`.
 */
export function readActorType(): ActorType {
  return getActorType();
}

// ============================================================
// Session transitions
// ============================================================
// The three ways a session's actor can change. Callers name the transition;
// this module owns what it implies.
//
// These exist because the alternative - exporting `setActorType` /
// `clearActorType` and letting each page assemble the consequences - spread one
// privacy rule across four call sites, and it was already got wrong once (only
// `/logout` cleared the actor, so an operator who redeemed a link on their own
// machine stayed blacked out on every later session of their own). The ordering
// constraint below is load-bearing and nothing in a type can enforce it, so it
// lives here instead of in a comment on each caller.

/**
 * A concierge link was redeemed: this session belongs to `actorType`.
 *
 * Store the actor **first**, then branch - an operator must be suppressed before
 * anything else can capture, and the stored actor is what keeps later page loads
 * dark from the start rather than dark from the first redemption.
 *
 * For an operator this records nothing at all, ever (KAN-16 decision 8, as
 * revised): they shop as the salon's own customer id, so capturing their session
 * would bind their cart-building to the salon's person and fill the salon's
 * history with searches and product views they never made. Labelling cannot
 * undo that; only not recording can.
 *
 * For a salon it initialises PostHog, reports the `/enter` pageview and the
 * funnel entry, and starts replay. All of that starts **here** rather than at
 * app-shell mount because until this call the actor was unknown and defaulted to
 * the salon - starting earlier would send an operator's `/flags` request and
 * `$pageview` (and the opening seconds of a recording), which is exactly what the
 * blackout prevents. `AnalyticsProvider` therefore leaves `/enter` entirely alone
 * (KAN-40, ADR-0006), so an operator's redemption makes no PostHog call at all.
 */
export function beginConciergeSession(actorType: ActorType): void {
  setActorType(actorType);

  if (actorType === "concierge_operator") {
    // Nothing has initialised on `/enter`, so this only latches the blackout for
    // the rest of the page.
    suppressAnalytics();
    return;
  }

  initAnalytics();
  analytics.pageview();
  analytics.conciergeLinkOpened({ actor_type: actorType });
  startSessionReplay();
}

/**
 * A concierge link could not be redeemed (missing, expired, replaced or the
 * exchange failed), so the session's actor is still unknown.
 *
 * Starts analytics anyway, as the anonymous visitor they are: nothing has been
 * identified, so there is no salon person for the visit to be bound to, and "the
 * link was dead" is a funnel drop-off the pilot needs to see. The operator
 * blackout still holds, because `initAnalytics` refuses for a stored operator
 * actor. Replay is not started: an unredeemed link has no settled salon actor.
 */
export function failConciergeSession(): void {
  initAnalytics();
  analytics.pageview();
}

/**
 * A password login succeeded: this session is the salon acting for themselves.
 *
 * Drops any actor a concierge redemption left in this browser. Without it an
 * operator who redeems links on their own machine stays blacked out on every
 * later session of their own, and their real activity vanishes from analytics
 * entirely.
 *
 * Does **not** lift the current page's suppression: `suppressed` is a
 * page-lifetime latch by design (see {@link suppressAnalytics}), so an operator
 * who signs in without a reload stays dark until the next page load. That is the
 * conservative direction to fail in, and it is why this clears the *stored*
 * actor rather than trying to re-initialise.
 */
export function beginSelfServeSession(): void {
  clearActorType();
}

/**
 * The session ended (logout).
 *
 * Clears the stored actor so the next person on this browser is not attributed
 * to this one - a shared salon tablet is the expected case, not an edge case -
 * and resets the PostHog identity, so whoever logs in next on this tab starts
 * from a fresh anonymous id instead of being merged into this person.
 *
 * Safe alongside `AnalyticsProvider`'s own reset when the profile query fails
 * with a 401: {@link resetIdentity} only acts on an *identified* person, so
 * whichever runs second finds an anonymous one and does nothing, and the
 * anonymous id rotates once per logout, not twice.
 */
export function endSession(): void {
  clearActorType();
  resetIdentity();
}

/**
 * Bind subsequent events to a stable person, keyed by the Medusa customer id.
 * PostHog aliases the current anonymous id under this distinct id, so pre-login
 * browsing stitches to the account. `setOnceProperties` are non-identifying
 * segments only (never email/name) and are written once per person.
 */
export function identifyUser(
  distinctId: string,
  setOnceProperties?: Record<string, unknown>
): void {
  if (!initialized) return;
  posthog.identify(distinctId, undefined, setOnceProperties);
}

/**
 * Whether PostHog currently holds an identified person (an `identify` call this
 * page load or persisted from an earlier one), as opposed to an anonymous visitor.
 * False while analytics is off.
 */
export function isUserIdentified(): boolean {
  // The public `get_property`, not posthog's private `_isIdentified()`, which
  // can change on any minor release. Both read the same persisted `$user_state`
  // ("identified" once `identify` has run, "anonymous" after `reset`); with the
  // default persistence this module uses they agree.
  return (
    initialized && posthog.get_property(USER_STATE_PROPERTY) === "identified"
  );
}

/**
 * Drop the identified person so a later user on the same browser is not merged
 * into the previous identity, and assign a fresh anonymous id.
 *
 * Idempotent and a no-op for an anonymous visitor: resetting one would churn the
 * anonymous id and break the stitching of their pre-login browsing onto the
 * account they log into. That is also what lets the logout transition and the
 * provider's session-expiry check both call it without rotating twice.
 */
export function resetIdentity(): void {
  if (!isUserIdentified()) return;
  posthog.reset();
}

/**
 * The URL a `$pageview` is sent with: origin and pathname only, exactly as
 * `before_send` leaves it. Exported so callers that dedupe pageviews compare the
 * URL that is actually sent, not the raw one - two locations that differ only in
 * their query string or fragment are the same page to PostHog.
 */
export function currentPageUrl(): string {
  return sanitiseUrl(window.location.href);
}

let previousPathname: string | undefined;

/**
 * Pages a redirect sends the visitor through before returning them: the sign-in
 * and first-login gates (`/nope` -> `/login?redirect=/nope` -> `/nope`, and the
 * same round trip through `/activate` and `/onboarding`), plus the other
 * standalone auth screens. None of them can hold a dead link a visitor followed,
 * so they must not be reported as "where the visitor came from".
 */
const NON_ORIGIN_PATHS: readonly string[] = [
  LOGIN_PATH,
  LOGOUT_PATH,
  ENTER_PATH,
  RESET_PASSWORD_PATH,
  SUPPLIER_MATCH_CONFIRM_PATH,
  ACTIVATE_PATH,
  ONBOARDING_PATH
];

function isNonOriginPath(pathname: string): boolean {
  return NON_ORIGIN_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

/**
 * Remember the in-app pathname the router just navigated away from (`undefined`
 * clears it). Called by `AnalyticsProvider` on each navigation that changes the
 * path, so events that care about "where did they come from within the app"
 * (`not_found_viewed.from_path`) can read it.
 *
 * Pathname only, by construction: any query string or fragment is stripped on
 * the way in. A standalone page (`NON_ORIGIN_PATHS`) **clears** the value
 * rather than leaving the earlier one in place: after `/nope` -> `/login` ->
 * `/nope` the page before the second `/nope` is not an in-app page that linked
 * to it, and keeping the stale `/nope` would report the 404 as its own origin.
 */
export function setPreviousPathname(pathname: string | undefined): void {
  const path = pathname ? sanitiseUrl(pathname) : "";
  previousPathname = path && !isNonOriginPath(path) ? path : undefined;
}

/**
 * The in-app pathname before the current one, or `undefined` on the first page
 * of a visit (nothing to come from within the app).
 */
export function getPreviousPathname(): string | undefined {
  return previousPathname;
}

/**
 * Validate (dev only) and forward an event to PostHog. Silently no-ops when
 * analytics is disabled, and drops malformed events in development after
 * logging, so a bad payload never reaches production data.
 */
function captureEvent<T>(schema: z.ZodType<T>, name: string, payload: T): void {
  if (!initialized) return;

  if (import.meta.env.DEV) {
    const result = schema.safeParse(payload);
    if (!result.success) {
      console.error(`[analytics] invalid payload for "${name}"`, result.error);
      return;
    }
  }

  posthog.capture(name, payload as Record<string, unknown>);
}

/**
 * Report an error that an error boundary or the router already caught, as a
 * PostHog `$exception` (KAN-248) and, once Sentry is initialised, as a Sentry
 * event too (see `captureCaughtError` in `../sentry`).
 *
 * `capture_exceptions` only hooks `window.onerror` and unhandled rejections, and
 * React hands a caught render error to the nearest boundary instead of
 * rethrowing it - so without this call every error the user actually sees a
 * fallback for is invisible to error tracking.
 *
 * The PostHog half goes through the same gates as every other capture: nothing
 * is sent to PostHog when analytics is off, unconfigured or suppressed. The
 * Sentry half has its own switch (Sentry must be initialised) and does not wait
 * on PostHog. Neither half reports an operator's session. It never throws - it
 * runs inside `componentDidCatch`, where a second error would take down the very
 * boundary that is trying to contain the first.
 *
 * Sends the error's own message and stack plus two dimensions of ours: the
 * `boundary` that caught it and the route `pathname`. The query string is left
 * out on purpose - search terms and tokens live there. The `$current_url`
 * posthog stamps is stripped of it by the `before_send` hook, as on every
 * event. No component stack, no props. The error's own message is
 * sent as written, so it is only as clean as the code that threw it (URLs inside
 * it are still scrubbed by the `before_send` hook, like everything else).
 */
export function captureCaughtException(
  error: unknown,
  context: { boundary: string }
): void {
  const routePath = window.location.pathname;

  // Sentry gets the same error with the same dimensions (see `lib/sentry`). It
  // has its own switch - nothing is sent until Sentry is initialised, which
  // needs a DSN and a deployed environment - so it must not wait on PostHog
  // being configured. It does honour the operator blackout, though: errors we
  // report from an operator's session are not sent. (Errors Sentry catches on
  // its own, outside this function, are not covered - see `lib/sentry`.)
  if (!isOperatorSession()) {
    captureCaughtError(error, {
      boundary: context.boundary,
      route_path: routePath
    });
  }

  if (!initialized) return;

  try {
    posthog.captureException(error, {
      boundary: context.boundary,
      route_path: routePath
    });
  } catch (reportingError) {
    console.error("[analytics] failed to report exception", reportingError);
  }
}

/**
 * Whether this session is the concierge operator's, whether or not analytics got
 * as far as noticing. `suppressAnalytics` sets `suppressed` when a concierge
 * session begins, but on any later page load only `initAnalytics` restores it,
 * and that never runs without a PostHog key - so the stored actor is checked as
 * well.
 */
function isOperatorSession(): boolean {
  return suppressed || getActorType() === "concierge_operator";
}

// ============================================================
// Reorder decision-capture (#54, ADR-0003)
// ============================================================
// Every reorder *decision* event - reorders, saved-list curation, and the
// future supplier-swap / freight-top-up / substitution / chair-flag surfaces -
// carries this shared envelope, so decisions are captured uniformly across
// surfaces rather than per-feature. The envelope is the "Salon Brain" seed:
// labelled data the Phase 2 AI learns from. See `docs/adr/0003-*` for the full
// event catalog (current + planned).

/** Who made a reorder decision. Sourced from the session (see `./actor`): a
 *  concierge link reports whether it was minted for the salon or for an
 *  operator acting on their behalf, and both authenticate as the same customer.
 *  Anyone who did not arrive through a link is the salon. */
const decisionEnvelopeSchema = z.object({
  actor_type: z.enum(["salon_user", "concierge_operator"])
});

/**
 * The actor stamped on every decision event.
 *
 * A constant rather than a session lookup: under the operator blackout
 * (decision 8, as revised) an operator session records nothing at all, so
 * everything that reaches PostHog genuinely is the salon. Reading the session
 * here would be a distinction without a difference - and a misleading one, since
 * it implies operator-attributed events exist somewhere in the data.
 */
type DecisionEnvelope = z.infer<typeof decisionEnvelopeSchema>;

const SELF_SERVE_ACTOR: DecisionEnvelope = { actor_type: "salon_user" };

/**
 * Forward a reorder *decision* event, stamping the shared decision envelope
 * (the actor dimension) onto the event-specific payload. `schema` describes
 * only the event's own fields; the envelope is merged in and validated here so
 * every decision surface is captured with one consistent shape. Actor is spread
 * last so a payload can never shadow it.
 */
function captureDecision<T extends object>(
  schema: z.ZodType<T>,
  name: string,
  payload: T
): void {
  captureEvent(schema.and(decisionEnvelopeSchema), name, {
    ...payload,
    ...SELF_SERVE_ACTOR
  });
}

const pageviewSchema = z.object({
  $current_url: z.string()
  // `$prev_pageview_duration` is NOT set here. posthog's `PageViewManager` runs
  // off the event name rather than the `capture_pageview` config, so it stamps
  // route timing onto these manual captures itself - and its value overwrites
  // anything we pass under the same key (KAN-44 R3).
});

const searchSubmittedSchema = z.object({
  /** The committed search query. */
  query: z.string(),
  /** How the search was committed. */
  trigger: z.enum(["typed", "suggestion"])
});

type SearchSubmitted = z.infer<typeof searchSubmittedSchema>;

const searchResultsViewedSchema = z.object({
  /** The committed query whose results were shown. */
  query: z.string(),
  /** Number of results the query returned; 0 for a zero-result search. */
  results_count: z.number().int().nonnegative()
});

type SearchResultsViewed = z.infer<typeof searchResultsViewedSchema>;

const searchResultClickedSchema = z.object({
  /** The typeahead query that produced the clicked suggestion. */
  query: z.string(),
  /** Which kind of suggestion was clicked. */
  result_type: z.enum(["product", "brand", "category", "supplier"]),
  /** 0-based rank of the clicked suggestion within the dropdown. */
  position: z.number().int().nonnegative()
});

type SearchResultClicked = z.infer<typeof searchResultClickedSchema>;

const previousOrdersOpenedSchema = z.object({
  /**
   * How many previously-ordered products the shortcut carried into the results
   * page. Counted before Typesense filters out anything since discontinued, so
   * it measures the size of the salon's habitual basket rather than what
   * happened to be purchasable at click time.
   */
  product_count: z.number().int().nonnegative(),
  /** The trailing window the list was drawn from, in days. */
  window_days: z.number().int().positive()
});

type PreviousOrdersOpened = z.infer<typeof previousOrdersOpenedSchema>;

const externalCheckoutConfirmedSchema = z.object({
  /** The seller the cart is being handed off to. */
  seller_id: z.string(),
  /** The seller's platform (e.g. "shopify"). */
  platform: z.string(),
  /** Number of items included in the external checkout URL (mapped only). */
  item_count: z.number().int().nonnegative()
});

type ExternalCheckoutConfirmed = z.infer<
  typeof externalCheckoutConfirmedSchema
>;

const reorderConfirmedSchema = z.object({
  /** The past order the reorder was built from. */
  order_id: z.string(),
  /** The supplier the reorder targets (null when the order has no seller link). */
  seller_id: z.string().nullable(),
  /** Lines added to the cart (available + repriced). */
  added_count: z.number().int().nonnegative(),
  /** Repriced lines among those added. */
  repriced_count: z.number().int().nonnegative(),
  /** Lines skipped because no live offer exists. */
  unavailable_count: z.number().int().nonnegative(),
  /** Addable lines the buyer unchecked before confirming (ADR-0004). */
  deselected_count: z.number().int().nonnegative(),
  /** Selected lines whose quantity the buyer edited away from the original. */
  quantity_overridden_count: z.number().int().nonnegative(),
  /**
   * Which surface the reorder started from: the header's "Reorder last order"
   * (`header`), a Recent Orders row on the home page (`home_recent_orders`), or
   * a row on the Orders page (`orders_page`). Same decision from three entry
   * points, so a property rather than three event names - mirrors
   * `stock_reorder_confirmed.source`.
   */
  source: z.enum(["header", "home_recent_orders", "orders_page"]),
  /**
   * True when the buyer confirmed with Quick Buy (straight to checkout) rather
   * than Add to cart. Set from the confirm path, so it is true even when nothing
   * was addable and no navigation happened: it records the choice, not the
   * outcome (`added_count` carries the outcome).
   */
  quick_buy: z.boolean()
});

type ReorderConfirmed = z.infer<typeof reorderConfirmedSchema>;

/** The surfaces an order reorder can start from (`reorder_confirmed.source`). */
export type ReorderSource = ReorderConfirmed["source"];

const stockReorderConfirmedSchema = z.object({
  /**
   * Which stock-reorder surface placed it: a single due item's Reorder button
   * (`due_item`), the widget's "reorder all due" (`due_all`), or the hero
   * "Quick Reorder" (`hero`). Encoded as a property (not the event name) because
   * they are the same decision from three entry points.
   */
  source: z.enum(["due_item", "due_all", "hero"]),
  /** Tracked variants the salon asked to reorder. */
  requested_count: z.number().int().nonnegative(),
  /** Lines added to a cart (available + repriced). */
  added_count: z.number().int().nonnegative(),
  /** Repriced lines among those added. */
  repriced_count: z.number().int().nonnegative(),
  /** Lines skipped because no live offer could be resolved. */
  unavailable_count: z.number().int().nonnegative(),
  /** Distinct seller carts the reorder fanned out into. */
  cart_count: z.number().int().nonnegative(),
  /**
   * True when the buyer confirmed with Quick Buy (straight to checkout) rather
   * than Add to cart. Records the choice, not the outcome (`added_count`).
   */
  quick_buy: z.boolean()
});

type StockReorderConfirmed = z.infer<typeof stockReorderConfirmedSchema>;

const voiceOrderConfirmedSchema = z.object({
  /** Items the parse step extracted from the recording. */
  spoken_item_count: z.number().int().nonnegative(),
  /** Lines added to a cart (available + repriced). */
  added_count: z.number().int().nonnegative(),
  /** Repriced lines among those added. */
  repriced_count: z.number().int().nonnegative(),
  /** Lines skipped because no live offer could be resolved. */
  unavailable_count: z.number().int().nonnegative(),
  /** Distinct seller carts the order fanned out into. */
  cart_count: z.number().int().nonnegative(),
  /**
   * Ambiguous items the buyer resolved (picked a variant), counted per spoken
   * item before variant-level collapsing. See `countAmbiguousResolved`.
   */
  ambiguous_resolved: z.number().int().nonnegative(),
  /**
   * Exact matches the buyer kept included, counted per spoken item before
   * variant-level collapsing. See `countExactConfirmed`.
   */
  exact_confirmed: z.number().int().nonnegative()
});

type VoiceOrderConfirmed = z.infer<typeof voiceOrderConfirmedSchema>;

// --- Saved Lists (F3) ---
// Reorder-decision events (Salon Brain seed): curating a saved list IS a
// reorder decision. Emitted via captureDecision so they carry the shared
// envelope (#54 / ADR-0003), alongside reorder_confirmed above.

const savedListCreatedSchema = z.object({
  /** The created list. */
  list_id: z.string(),
  /** How the list was created. */
  source: z.enum(["scratch", "from_cart"]),
  /** Lines the list was seeded with. */
  item_count: z.number().int().nonnegative()
});

type SavedListCreated = z.infer<typeof savedListCreatedSchema>;

const savedListItemQuantityChangedSchema = z.object({
  /** The edited list. */
  list_id: z.string(),
  /** The new saved quantity (0 = the line was removed). */
  quantity: z.number().int().nonnegative()
});

type SavedListItemQuantityChanged = z.infer<
  typeof savedListItemQuantityChangedSchema
>;

const savedListAddedToCartSchema = z.object({
  /** The list added to the cart. */
  list_id: z.string(),
  /** Lines added (available). */
  added_count: z.number().int().nonnegative(),
  /** Lines skipped (unavailable / discontinued). */
  unavailable_count: z.number().int().nonnegative(),
  /** Distinct seller carts the list fanned out into. */
  cart_count: z.number().int().nonnegative(),
  /** Addable lines the buyer unchecked before confirming (ADR-0004). */
  deselected_count: z.number().int().nonnegative(),
  /** Selected lines whose quantity the buyer edited away from the saved value. */
  quantity_overridden_count: z.number().int().nonnegative()
});

type SavedListAddedToCart = z.infer<typeof savedListAddedToCartSchema>;

const savedListItemsAddedSchema = z.object({
  /** The list items were added to. */
  list_id: z.string(),
  /** Lines added in this action. */
  item_count: z.number().int().nonnegative(),
  /** Where the add originated (e.g. a product page). */
  source: z.enum(["product"])
});

type SavedListItemsAdded = z.infer<typeof savedListItemsAddedSchema>;

const favoriteToggledSchema = z.object({
  /** The favourited/unfavourited variant. */
  variant_id: z.string(),
  /** The seller routing key for that variant. */
  seller_id: z.string(),
  /** Whether the line is favourited after the toggle. */
  favorited: z.boolean(),
  /** Where the heart icon that fired this was rendered. */
  source: z.enum(["pdp"])
});

type FavoriteToggled = z.infer<typeof favoriteToggledSchema>;

// --- Concierge link pilot (KAN-16 / KAN-40) ---
// The funnel the WhatsApp pilot is measured on: link opened -> matrix viewed ->
// supplier switched -> checkout started -> order confirmed -> password set.
//
// None of these carries an actor dimension, and that is deliberate: under the
// operator blackout an operator session records nothing at all, so everything
// here is the salon by construction. The `actor_type` super-property that an
// earlier draft of KAN-40 proposed was reverted - see `initAnalytics`.

const conciergeLinkOpenedSchema = z.object({
  /** Who the redeemed link was minted for - top of the funnel, and the
   *  authoritative source of this session's actor. */
  actor_type: z.enum(["salon_user", "concierge_operator"])
});

type ConciergeLinkOpened = z.infer<typeof conciergeLinkOpenedSchema>;

const quoteDiffViewedSchema = z.object({
  /** Lines still buyable at the quoted price. */
  unchanged_count: z.number().int().nonnegative(),
  /** Lines whose price moved since the quote. */
  repriced_count: z.number().int().nonnegative(),
  /** Lines that can no longer be bought at all. */
  unavailable_count: z.number().int().nonnegative()
});

type QuoteDiffViewed = z.infer<typeof quoteDiffViewedSchema>;

const supplierSwitchedSchema = z.object({
  /** The product whose sourcing changed. */
  product_id: z.string(),
  /** The variant the switch was made on. */
  variant_id: z.string(),
  /** The supplier the line was sourced from before. */
  from_seller_id: z.string(),
  /** The supplier it is sourced from now. */
  to_seller_id: z.string(),
  /** Unit price before the switch, in major units. */
  from_unit_price: z.number(),
  /** Unit price after the switch, in major units. */
  to_unit_price: z.number(),
  /** `to - from`: negative means the salon saved money. The reason this event
   *  exists - it is the pilot's evidence that comparison changes behaviour. */
  price_delta: z.number(),
  /**
   * True when this switch is the salon undoing the previous one via the
   * confirmation toast.
   *
   * Undo is implemented as a switch back, so without this flag a reverted
   * switch is indistinguishable from a kept one and inflates the pilot's
   * headline number: A -> B -> A counts as two switches when the salon changed
   * nothing. Net switches are `count(is_undo = false) - count(is_undo = true)`.
   */
  is_undo: z.boolean()
});

type SupplierSwitched = z.infer<typeof supplierSwitchedSchema>;

const comparisonMatrixViewedSchema = z.object({
  /** Distinct products shown as rows. */
  line_count: z.number().int().nonnegative(),
  /** Suppliers shown as columns - every supplier carrying the cart's products,
   *  not only those already holding a cart. */
  supplier_count: z.number().int().nonnegative()
});

type ComparisonMatrixViewed = z.infer<typeof comparisonMatrixViewedSchema>;

const orderConfirmedSchema = z.object({
  /** Seller carts completed into orders and reported by this event. */
  cart_count: z.number().int().nonnegative(),
  /** Line items across those carts. */
  item_count: z.number().int().nonnegative(),
  /** Total of those carts in major units. */
  total: z.number().nonnegative(),
  /** The order currency. */
  currency_code: z.string(),
  /**
   * Seller carts of this checkout still not completed when the event fired: 0 on
   * a full success, above 0 when a later seller's payment or completion failed
   * after earlier carts had already become real orders.
   */
  failed_cart_count: z.number().int().nonnegative(),
  /**
   * True when `failed_cart_count` is above 0 - the checkout only partly went
   * through. Sums across events stay correct: a retry reports only the carts it
   * completes itself, never the ones an earlier attempt already reported.
   */
  partial: z.boolean()
});

type OrderConfirmed = z.infer<typeof orderConfirmedSchema>;

// --- Self-serve funnel (KAN-44 §5B, built in KAN-50) ---
// Steps 3-5 of the six-step catalogue funnel. Steps 1 and 2 need no code: step
// 1 is the session's first `$pageview`, step 2 is a saved PostHog Action over
// `search_submitted` / `search_results_viewed`, because a funnel step cannot
// express an OR of two events any other way.
//
// These also close two holes in the concierge funnel: bulk adds (reorder, saved
// lists, stock reorder) were instrumented long ago while **manual** single-item
// adds were invisible, and nothing at all marked reaching checkout.

const productViewedSchema = z.object({
  /** The offer whose detail was opened - product, variant and seller in one. */
  offer_id: z.string(),
  /** Present only in the multi-variant (shade-matrix) flow, which is the only
   *  caller that knows the product before the dialog loads it. */
  product_id: z.string().nullable(),
  /** Same caveat as `product_id`. */
  seller_id: z.string().nullable(),
  /** Whether the shade-matrix grid opened rather than the single-offer view.
   *  The two are different amounts of work for the salon, so a funnel that
   *  mixed them would hide which one stalls. */
  multi_variant: z.boolean()
});

type ProductViewed = z.infer<typeof productViewedSchema>;

/**
 * One event per **offer**, not per add action. The shade-matrix flow adds each
 * chosen variant separately, so confirming six shades in one click emits six of
 * these. That is the count the funnel wants, but it means `count(add_to_cart)`
 * is not "number of times somebody added something".
 */
const addToCartSchema = z.object({
  /** The offer added. */
  offer_id: z.string(),
  /** The seller cart it routed to (resolved server-side from the offer). */
  seller_id: z.string(),
  /** Units added in this action. */
  quantity: z.number().int().positive(),
  /** True when this add opened a new seller cart, i.e. a supplier the salon was
   *  not already buying from in this basket. */
  is_new_cart: z.boolean(),
  /**
   * True when the add came from a Quick Buy button in the product dialog, which
   * adds and then goes straight to checkout, skipping the cart page. Lets funnels
   * split the Quick Buy path from the cart path without a new event:
   * `checkout_started` carries no such flag because it fires from the checkout
   * route, which cannot tell how the buyer got there. Reorder's Quick Buy is
   * recorded on `reorder_confirmed.quick_buy` instead (that flow never reaches
   * this event).
   */
  quick_buy: z.boolean()
});

type AddToCart = z.infer<typeof addToCartSchema>;

const checkoutStartedSchema = z.object({
  /** Seller carts taken into checkout together. */
  cart_count: z.number().int().nonnegative(),
  /** Line items across those carts. */
  item_count: z.number().int().nonnegative(),
  /** Basket total in major units, at the moment checkout opened - i.e. the
   *  value at risk if they drop here. */
  total: z.number().nonnegative(),
  /**
   * The basket currency, taken off the seller-cart payload (every cart inherits
   * the buyer's region, so any one of them answers). Nullable only for the
   * surface that fires this with no cart to read it from - it stopped needing a
   * fetch of its own once the payload started carrying the currency (KAN-47).
   */
  currency_code: z.string().nullable()
});

type CheckoutStarted = z.infer<typeof checkoutStartedSchema>;

/** The `password_set` source values, defined once for the schema and callers. */
export const PASSWORD_SET_SOURCES = ["activation", "first_login_gate"] as const;

/** A bare `/activate`: the approval email's link (KAN-293). */
export const PASSWORD_SET_ACTIVATION_SOURCE = PASSWORD_SET_SOURCES[0];

/** The first-login gate sent a passwordless salon session to set one (KAN-306). */
export const PASSWORD_SET_GATE_SOURCE = PASSWORD_SET_SOURCES[1];

const passwordSetSchema = z.object({
  /** Which way into the set-password step; see `PASSWORD_SET_SOURCES`. */
  source: z.enum(PASSWORD_SET_SOURCES)
});

type PasswordSet = z.infer<typeof passwordSetSchema>;

const supportWhatsAppOpenedSchema = z.object({
  /** Curated page slug the salon reached out from (see `lib/support`). */
  page: z.string()
});

type SupportWhatsAppOpened = z.infer<typeof supportWhatsAppOpenedSchema>;

const notFoundViewedSchema = z.object({
  /**
   * The unmatched pathname. Never the query string or fragment. It is a raw
   * pathname: `before_send` only rewrites absolute URLs, so a relative path is
   * NOT scrubbed. It is the same pathname already sent as `$current_url` on
   * `$pageview`, so this adds no new exposure.
   */
  path: z.string(),
  /**
   * The **entry** referrer: `document.referrer`, i.e. the page that sent the
   * visitor to the app in the first place, or an empty string when there is none
   * (a typed or bookmarked address). In a single-page app it does not change on
   * in-app navigation, so it says nothing about which in-app page held a dead
   * link - that is `from_path`. An absolute URL is reduced to origin + pathname
   * by the `before_send` hook like any other.
   */
  referrer: z.string(),
  /**
   * The in-app pathname the visitor navigated from (pathname only, never the
   * query string or fragment). Omitted when there is none: the 404 was the first
   * page of the visit, a reload, or the visitor arrived via a standalone page
   * such as `/login` (see `setPreviousPathname`). Like `path`, a raw pathname
   * that `before_send` does not scrub, and already visible as a `$current_url`.
   */
  from_path: z.string().optional()
});

type NotFoundViewed = z.infer<typeof notFoundViewedSchema>;

/**
 * Typed analytics event catalog. Add new events as methods here so every call
 * site stays type-checked and the full event vocabulary lives in one place.
 */
export const analytics = {
  /**
   * Manually captured page view. Wired to TanStack Router navigation so every
   * resolved route change is recorded with the current URL.
   */
  pageview(): void {
    captureEvent(pageviewSchema, "$pageview", {
      $current_url: currentPageUrl()
    });
  },

  /**
   * A search was committed - either typed and submitted, or by selecting a
   * suggestion. Fire only at commit, never on the as-you-type typeahead.
   */
  searchSubmitted(payload: SearchSubmitted): void {
    captureEvent(searchSubmittedSchema, "search_submitted", payload);
  },

  /**
   * The results for a committed search settled. Carries the result count
   * (including 0) so zero-result and low-result searches are measurable.
   */
  searchResultsViewed(payload: SearchResultsViewed): void {
    captureEvent(searchResultsViewedSchema, "search_results_viewed", payload);
  },

  /**
   * A specific typeahead suggestion (product/brand/category/supplier) was
   * clicked or keyboard-selected. Pairs with search_submitted and
   * search_results_viewed to derive search click-through.
   */
  searchResultClicked(payload: SearchResultClicked): void {
    captureEvent(searchResultClickedSchema, "search_result_clicked", payload);
  },

  /**
   * The salon opened the "Previous orders" quick action from the search
   * dropdown (KAN-51). Deliberately its own event rather than a `trigger`
   * variant of `search_submitted`: this is the first personalized quick action
   * (#8's Phase 2), and its adoption has to be measurable on its own rather
   * than buried in generic search volume. `search_submitted` is NOT also fired
   * for it - there is no query to attribute.
   */
  previousOrdersOpened(payload: PreviousOrdersOpened): void {
    captureEvent(previousOrdersOpenedSchema, "previous_orders_opened", payload);
  },

  /**
   * The salon confirmed the external-checkout interstitial and is being
   * redirected to the seller's own store. This is a client interaction event:
   * the authoritative handoff event (`external_checkout_redirected`) is emitted
   * server-side, so this uses a distinct name to avoid double-counting.
   */
  externalCheckoutConfirmed(payload: ExternalCheckoutConfirmed): void {
    captureEvent(
      externalCheckoutConfirmedSchema,
      "external_checkout_confirm_clicked",
      payload
    );
  },

  /**
   * The salon confirmed a reorder and the available/repriced lines were added to
   * the cart. This is the load-bearing event for the roadmap's "reorder loop"
   * (validates "≥4 reorders/mo") and feeds personalized search (#8). Fire only
   * on a successful confirm, not on the preview.
   */
  reorderConfirmed(payload: ReorderConfirmed): void {
    captureDecision(reorderConfirmedSchema, "reorder_confirmed", payload);
  },

  /**
   * The salon confirmed a variant-keyed reorder from opt-in stock tracking
   * (api#152 / #72 / #73) - a due item's Reorder, the widget's "reorder all", or
   * the hero "Quick Reorder" - and the resolved lines were added across the
   * seller cart(s). A distinct event from `reorder_confirmed` (that one is keyed
   * to a past order; this one to tracked variants) so the two reorder loops stay
   * separable in funnels, while both count toward the roadmap's reorder metric.
   */
  stockReorderConfirmed(payload: StockReorderConfirmed): void {
    captureDecision(
      stockReorderConfirmedSchema,
      "stock_reorder_confirmed",
      payload
    );
  },

  /**
   * The salon confirmed a voice-parsed order (KAN-143) and the resolved lines
   * were added across the seller cart(s). A distinct event from the reorder
   * loops so the voice entry point stays separable in funnels, while still
   * counting toward the roadmap's cart-add metric.
   */
  voiceOrderConfirmed(payload: VoiceOrderConfirmed): void {
    captureEvent(voiceOrderConfirmedSchema, "voice_order_confirmed", payload);
  },

  /** A saved list was created - from scratch (#59) or from the cart (#60). */
  savedListCreated(payload: SavedListCreated): void {
    captureDecision(savedListCreatedSchema, "saved_list_created", payload);
  },

  /** A saved-list line's quantity was edited (0 = removed). */
  savedListItemQuantityChanged(payload: SavedListItemQuantityChanged): void {
    captureDecision(
      savedListItemQuantityChangedSchema,
      "saved_list_item_quantity_changed",
      payload
    );
  },

  /** A saved list was applied to the cart (fanned out into seller carts). */
  savedListAddedToCart(payload: SavedListAddedToCart): void {
    captureDecision(
      savedListAddedToCartSchema,
      "saved_list_added_to_cart",
      payload
    );
  },

  /** Product variants were added to a saved list (e.g. from a product page). */
  savedListItemsAdded(payload: SavedListItemsAdded): void {
    captureDecision(
      savedListItemsAddedSchema,
      "saved_list_items_added",
      payload
    );
  },

  /**
   * The single-tap heart icon (KAN-14) flipped a line's favourite state.
   * Favourites is a saved list under the hood (api ADR-0002), so this joins
   * the saved-list events as a reorder decision.
   */
  favoriteToggled(payload: FavoriteToggled): void {
    captureDecision(favoriteToggledSchema, "favorite_toggled", payload);
  },

  /**
   * The salon opened the WhatsApp support hand-off (KAN-13) - the FAB on an
   * authenticated page, or the plain-text link on the login page. This is an
   * intent marker, not a delivery guarantee: it counts everyone who opens
   * WhatsApp, including the people who never send - who are invisible to us
   * once WhatsApp takes over.
   */
  supportWhatsAppOpened(payload: SupportWhatsAppOpened): void {
    captureEvent(
      supportWhatsAppOpenedSchema,
      "support_whatsapp_opened",
      payload
    );
  },

  /**
   * The salon landed on the "we couldn't find that page" screen. Surfaces dead
   * links and mistyped addresses, which are otherwise only visible as an
   * ordinary `$pageview` of an unknown path. `referrer` is the entry (document)
   * referrer; `from_path` is the in-app page the visitor clicked from, which is
   * the one that points at the broken link (see {@link getPreviousPathname}).
   * `before_send` reduces an absolute `referrer` to origin + pathname, but it
   * does NOT scrub `path` or `from_path`: they are raw relative pathnames, the
   * same pathname already sent as `$current_url` on `$pageview`, so no new
   * exposure. An outcome, not a reorder decision, so no decision envelope.
   */
  notFoundViewed(payload: NotFoundViewed): void {
    captureEvent(notFoundViewedSchema, "not_found_viewed", payload);
  },

  /**
   * A concierge link was redeemed - the top of the WhatsApp pilot funnel, and
   * the only place we learn whether this session is the salon or an operator.
   * Fired after the exchange succeeds, never on a failed or expired link.
   */
  conciergeLinkOpened(payload: ConciergeLinkOpened): void {
    captureEvent(conciergeLinkOpenedSchema, "concierge_link_opened", payload);
  },

  /**
   * The review-matrix tab opened.
   *
   * Gives `supplier_switched` a denominator. "Opened the grid, compared, and
   * did not switch" is the most common outcome and is otherwise invisible, so
   * without this the ticket's own thesis - that putting suppliers side by side
   * changes what a salon buys - could not be evidenced either way.
   */
  comparisonMatrixViewed(payload: ComparisonMatrixViewed): void {
    captureEvent(
      comparisonMatrixViewedSchema,
      "comparison_matrix_viewed",
      payload
    );
  },

  /**
   * The salon was shown what moved since their quote. Carries the tally rather
   * than the lines, so drift is measurable across the pilot without putting
   * per-product prices in the analytics stream. Fired once per landing, and
   * only when something actually changed.
   */
  quoteDiffViewed(payload: QuoteDiffViewed): void {
    captureEvent(quoteDiffViewedSchema, "quote_diff_viewed", payload);
  },

  /**
   * The salon re-sourced a product to a different supplier from the comparison
   * grid. **The money event**: the pilot's thesis is that easy cross-supplier
   * comparison changes what salons buy, and this is the only direct evidence of
   * that. A reorder decision, so it carries the shared decision envelope.
   */
  supplierSwitched(payload: SupplierSwitched): void {
    captureDecision(supplierSwitchedSchema, "supplier_switched", payload);
  },

  /**
   * A cart was completed **internally** into a real Order - the pilot's primary
   * conversion event, since phase one runs on manual bank transfer.
   *
   * Deliberately NOT fired on the external-checkout path: that is already
   * covered by `external_checkout_confirm_clicked` (client) and
   * `external_checkout_redirected` (server, authoritative). One event spanning
   * both would double-count the external path and conflate "ordered" with
   * "handed off to the supplier".
   *
   * Fired for the carts that **did** complete, including when a later seller's
   * payment fails after earlier ones became real orders (`partial: true`);
   * `cart_count` / `item_count` / `total` cover those completed carts only.
   */
  orderConfirmed(payload: OrderConfirmed): void {
    captureEvent(orderConfirmedSchema, "order_confirmed", payload);
  },

  /**
   * The salon set a password, so they can return unaided. The pilot's
   * activation event - and the thing that triggers the welcome email, since
   * that is now hung off activation rather than account creation.
   */
  passwordSet(payload: PasswordSet): void {
    captureEvent(passwordSetSchema, "password_set", payload);
  },

  /**
   * A product's detail opened - the PDP equivalent here, since browsing opens a
   * dialog rather than navigating. An *outcome*, not a reorder decision, so it
   * goes through `captureEvent` and carries no decision envelope.
   */
  productViewed(payload: ProductViewed): void {
    captureEvent(productViewedSchema, "product_viewed", payload);
  },

  /**
   * A single item was added to a seller cart by hand.
   *
   * Fired from the mutation rather than its call sites, so every manual add is
   * captured wherever it originates. Bulk adds deliberately do NOT reach here -
   * reorders, saved lists and stock reorders each have their own decision event
   * carrying counts, and routing them through this one as well would double-
   * count the basket and drown the manual signal this event exists to expose.
   */
  addToCart(payload: AddToCart): void {
    captureEvent(addToCartSchema, "add_to_cart", payload);
  },

  /**
   * The salon opened checkout with a basket - funnel step 5, and the step the
   * pilot would otherwise be blind to. With `enable_external_checkout: false`
   * for the pilot, `external_checkout_confirm_clicked` never fires, so without
   * this there is no signal at all that anybody reached checkout.
   *
   * Fires once per checkout entry, not per render or per step change: the
   * question is how many salons got here, not how long they stayed.
   */
  checkoutStarted(payload: CheckoutStarted): void {
    captureEvent(checkoutStartedSchema, "checkout_started", payload);
  }
};
